import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Check, Sparkles, Calendar, X, ChevronDown, ChevronUp, Target, Edit3 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTED_EMOJI = ['✨', '🌸', '🏡', '✈️', '📚', '💐', '🎨', '🏃', '💼', '💛', '🎯', '🌿', '🎬', '🎸', '💖', '🌊'];

function daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(iso + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target - now) / (1000 * 60 * 60 * 24));
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function DreamsTab() {
  const [dreams, setDreams] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newEmoji, setNewEmoji] = useState('✨');
  const [newDate, setNewDate] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dreams`, { withCredentials: true });
      setDreams(data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const t = newTitle.trim();
    if (!t) { toast.error('Ge drömmen en titel 🌸'); return; }
    try {
      await axios.post(`${API}/dreams`,
        { title: t, emoji: newEmoji, target_date: newDate || null, description: newDesc.trim() || null },
        { withCredentials: true });
      setNewTitle(''); setNewEmoji('✨'); setNewDate(''); setNewDesc('');
      setCreating(false);
      load();
    } catch { toast.error('Kunde inte skapa'); }
  };

  const removeDream = async (id) => {
    if (!window.confirm('Ta bort drömmen?')) return;
    try {
      await axios.delete(`${API}/dreams/${id}`, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  const updateDream = async (id, patch) => {
    try {
      await axios.patch(`${API}/dreams/${id}`, patch, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  const addSubgoal = async (dreamId, text, targetDate) => {
    if (!text.trim()) return;
    try {
      await axios.post(`${API}/dreams/${dreamId}/subgoal`,
        { text: text.trim(), target_date: targetDate || null },
        { withCredentials: true });
      load();
    } catch { toast.error('Kunde inte lägga till delmål'); }
  };

  const toggleSubgoal = async (dreamId, sub) => {
    try {
      await axios.patch(`${API}/dreams/${dreamId}/subgoal/${sub.id}`,
        { done: !sub.done }, { withCredentials: true });
      if (!sub.done) toast.success(`✨ +2 glädje! Ett steg närmare`, { duration: 1500 });
      load();
    } catch { /* ignore */ }
  };

  const removeSubgoal = async (dreamId, subId) => {
    try {
      await axios.delete(`${API}/dreams/${dreamId}/subgoal/${subId}`, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  const updateSubgoal = async (dreamId, subId, patch) => {
    try {
      await axios.patch(`${API}/dreams/${dreamId}/subgoal/${subId}`, patch, { withCredentials: true });
      load();
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-5" data-testid="dreams-tab">
      <div className="neo-card p-5 bg-gradient-to-br from-[#F5EEFF] via-[#E8F7FF] to-[#FFE8F5]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#C8B6FF] border-2 border-black flex items-center justify-center">
            <Target className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="text-2xl font-bold font-['Fredoka']">Mål & Drömmar 🎯✨</h2>
            <p className="text-sm text-gray-700">Stora drömmar byggs av små steg. Bryt ner dem här 💛</p>
          </div>
        </div>
      </div>

      {/* Create form */}
      {creating ? (
        <div className="neo-card p-4 bg-white space-y-3" data-testid="dreams-create-form">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newEmoji}
              onChange={(e) => setNewEmoji(e.target.value)}
              className="w-14 text-center text-xl neo-input"
              maxLength={4}
              data-testid="dreams-new-emoji"
            />
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Vad drömmer du om?"
              className="flex-1 neo-input"
              autoFocus
              data-testid="dreams-new-title"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {SUGGESTED_EMOJI.map((e) => (
              <button
                key={e}
                onClick={() => setNewEmoji(e)}
                className={`neo-btn w-9 h-9 text-lg ${newEmoji === e ? 'ring-2 ring-[#D06892]' : 'bg-white'}`}
                data-testid={`dreams-pick-emoji-${e}`}
              >
                {e}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 opacity-60" />
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="neo-input flex-1"
              data-testid="dreams-new-date"
            />
          </div>
          <textarea
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Beskrivning (valfri) — varför är denna dröm viktig för dig?"
            rows={2}
            className="w-full neo-input text-sm"
            data-testid="dreams-new-desc"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setCreating(false)} className="neo-btn px-3 py-1.5 bg-white text-sm">Avbryt</button>
            <button onClick={create} className="neo-btn px-4 py-1.5 bg-[#B9FBC0] font-bold text-sm" data-testid="dreams-new-save">
              Spara drömmen ✨
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setCreating(true)}
          className="neo-btn w-full py-3 bg-gradient-to-r from-[#FFC8DD] via-[#C8B6FF] to-[#A2D2FF] font-bold text-white flex items-center justify-center gap-2"
          data-testid="dreams-add-btn"
        >
          <Plus className="w-5 h-5" /> Ny dröm / nytt mål
        </button>
      )}

      {/* List */}
      <div className="grid md:grid-cols-2 gap-4">
        {dreams.length === 0 ? (
          <div className="neo-card p-6 text-center bg-white md:col-span-2">
            <p className="text-4xl mb-2">✨</p>
            <p className="text-sm opacity-70">Inga drömmar än — vad vill du uppnå?</p>
          </div>
        ) : dreams.map((d) => (
          <DreamCard
            key={d.id}
            dream={d}
            expanded={expandedId === d.id}
            onToggleExpand={() => setExpandedId(expandedId === d.id ? null : d.id)}
            onUpdate={(patch) => updateDream(d.id, patch)}
            onDelete={() => removeDream(d.id)}
            onAddSubgoal={(text, date) => addSubgoal(d.id, text, date)}
            onToggleSubgoal={(sub) => toggleSubgoal(d.id, sub)}
            onRemoveSubgoal={(subId) => removeSubgoal(d.id, subId)}
            onUpdateSubgoal={(subId, patch) => updateSubgoal(d.id, subId, patch)}
            editing={editingId === d.id}
            setEditing={(v) => setEditingId(v ? d.id : null)}
          />
        ))}
      </div>
    </div>
  );
}


function DreamCard({ dream, expanded, onToggleExpand, onUpdate, onDelete, onAddSubgoal, onToggleSubgoal, onRemoveSubgoal, onUpdateSubgoal, editing, setEditing }) {
  const [newSubText, setNewSubText] = useState('');
  const [newSubDate, setNewSubDate] = useState('');
  const [editTitle, setEditTitle] = useState(dream.title);
  const [editDate, setEditDate] = useState(dream.target_date || '');
  const [editDesc, setEditDesc] = useState(dream.description || '');

  useEffect(() => {
    setEditTitle(dream.title);
    setEditDate(dream.target_date || '');
    setEditDesc(dream.description || '');
  }, [dream]);

  const days = daysUntil(dream.target_date);
  const dateBadge = days === null ? null
    : days < 0 ? `Förfallet ${-days}d sedan`
    : days === 0 ? 'Idag!'
    : days === 1 ? 'Imorgon'
    : days <= 30 ? `${days} dagar kvar`
    : `${fmtDate(dream.target_date)}`;
  const dateColor = days === null ? '#F5EEFF'
    : days < 0 ? '#FFE8E8'
    : days <= 7 ? '#FFE8A3'
    : days <= 30 ? '#B9FBC0'
    : '#E8F7FF';

  return (
    <div className="neo-card p-4 bg-white" data-testid={`dream-card-${dream.id}`}>
      <div className="flex items-start gap-3">
        <div className="text-4xl flex-shrink-0">{dream.emoji}</div>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-2">
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full neo-input font-bold"
                data-testid={`dream-edit-title-${dream.id}`}
              />
              <input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="w-full neo-input text-sm"
              />
              <textarea
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                rows={2}
                className="w-full neo-input text-sm"
                placeholder="Beskrivning"
              />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEditing(false)} className="neo-btn px-2 py-1 bg-white text-xs">Avbryt</button>
                <button
                  onClick={async () => {
                    await onUpdate({ title: editTitle.trim() || dream.title, target_date: editDate || null, description: editDesc.trim() || null });
                    setEditing(false);
                  }}
                  className="neo-btn px-2 py-1 bg-[#B9FBC0] text-xs font-bold"
                  data-testid={`dream-save-edit-${dream.id}`}
                >Spara</button>
              </div>
            </div>
          ) : (
            <>
              <h3 className="font-bold font-['Fredoka'] text-lg truncate">{dream.title}</h3>
              {dateBadge && (
                <span
                  className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black mt-1"
                  style={{ backgroundColor: dateColor }}
                >
                  📅 {dateBadge}
                </span>
              )}
              {dream.description && (
                <p className="text-xs text-gray-600 mt-1 italic">{dream.description}</p>
              )}
            </>
          )}
        </div>
        {!editing && (
          <div className="flex flex-col gap-1">
            <button onClick={() => setEditing(true)} className="neo-btn p-1.5 bg-white" data-testid={`dream-edit-btn-${dream.id}`}>
              <Edit3 className="w-3 h-3" />
            </button>
            <button onClick={onDelete} className="neo-btn p-1.5 bg-white" data-testid={`dream-delete-${dream.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1 text-[11px]">
          <span className="font-bold opacity-70">Framsteg</span>
          <span className="font-bold">{dream.subgoals_done} / {dream.subgoals_total} delmål · {dream.progress_pct}%</span>
        </div>
        <div className="h-3 rounded-full bg-black/10 overflow-hidden border-2 border-black">
          <div
            className="h-full bg-gradient-to-r from-[#FFC8DD] via-[#C8B6FF] to-[#A2D2FF] transition-all duration-700"
            style={{ width: `${dream.progress_pct}%` }}
          />
        </div>
      </div>

      {/* Subgoals */}
      <button
        onClick={onToggleExpand}
        className="mt-3 w-full text-xs font-bold flex items-center justify-center gap-1 opacity-70 hover:opacity-100"
        data-testid={`dream-expand-${dream.id}`}
      >
        {expanded ? <><ChevronUp className="w-3 h-3" /> Göm delmål</> : <><ChevronDown className="w-3 h-3" /> Visa {dream.subgoals_total || 0} delmål</>}
      </button>

      {expanded && (
        <div className="mt-3 space-y-2" data-testid={`dream-subgoals-${dream.id}`}>
          {dream.subgoals.map((s) => (
            <SubgoalRow
              key={s.id}
              dreamId={dream.id}
              sub={s}
              onToggle={() => onToggleSubgoal(s)}
              onRemove={() => onRemoveSubgoal(s.id)}
              onUpdate={(patch) => onUpdateSubgoal(s.id, patch)}
            />
          ))}
          {/* Add sub */}
          <div className="neo-card p-2 bg-[#FFFEF5] space-y-2">
            <input
              type="text"
              value={newSubText}
              onChange={(e) => setNewSubText(e.target.value)}
              placeholder="Nästa lilla steg…"
              className="w-full neo-input text-sm"
              data-testid={`dream-newsub-text-${dream.id}`}
            />
            <div className="flex gap-2">
              <input
                type="date"
                value={newSubDate}
                onChange={(e) => setNewSubDate(e.target.value)}
                className="neo-input text-xs flex-1"
                data-testid={`dream-newsub-date-${dream.id}`}
              />
              <button
                onClick={() => { onAddSubgoal(newSubText, newSubDate); setNewSubText(''); setNewSubDate(''); }}
                className="neo-btn px-3 py-1 bg-[#B9FBC0] text-xs font-bold"
                data-testid={`dream-newsub-add-${dream.id}`}
              >
                <Plus className="w-3 h-3 inline" /> Lägg till
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SubgoalRow({ sub, onToggle, onRemove, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [txt, setTxt] = useState(sub.text);
  const [dt, setDt] = useState(sub.target_date || '');
  const days = daysUntil(sub.target_date);

  const save = async () => {
    await onUpdate({ text: txt.trim() || sub.text, target_date: dt || null });
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-2 neo-card p-2 bg-white" data-testid={`subgoal-${sub.id}`}>
      <button
        onClick={onToggle}
        className={`w-5 h-5 rounded-full border-2 border-black flex-shrink-0 flex items-center justify-center ${sub.done ? 'bg-[#B9FBC0]' : 'bg-white'}`}
        data-testid={`subgoal-toggle-${sub.id}`}
      >
        {sub.done && <Check className="w-3 h-3" strokeWidth={3} />}
      </button>
      {editing ? (
        <>
          <input value={txt} onChange={(e) => setTxt(e.target.value)} className="flex-1 neo-input text-sm" autoFocus />
          <input type="date" value={dt} onChange={(e) => setDt(e.target.value)} className="neo-input text-xs w-36" />
          <button onClick={save} className="neo-btn px-2 py-0.5 bg-[#B9FBC0] text-xs" data-testid={`subgoal-save-${sub.id}`}>Spara</button>
        </>
      ) : (
        <>
          <span className={`flex-1 text-sm ${sub.done ? 'line-through opacity-60' : ''}`}>{sub.text}</span>
          {sub.target_date && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full border border-black"
              style={{ backgroundColor: days < 0 ? '#FFE8E8' : days <= 7 ? '#FFE8A3' : '#E8F7FF' }}
            >
              {days === null ? fmtDate(sub.target_date) : days < 0 ? `${-days}d sen` : days === 0 ? 'Idag!' : `${days}d`}
            </span>
          )}
          <button onClick={() => setEditing(true)} className="neo-btn p-1 bg-white text-xs" data-testid={`subgoal-edit-${sub.id}`}>
            <Edit3 className="w-3 h-3" />
          </button>
          <button onClick={onRemove} className="neo-btn p-1 bg-white text-xs" data-testid={`subgoal-remove-${sub.id}`}>
            <X className="w-3 h-3" />
          </button>
        </>
      )}
    </div>
  );
}
